import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  firstValueFrom,
  from,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { HttpApiService } from '../providers/http-api/http-api.service';
import { VAPID_PUBLIC_KEY } from './push.config';
import { SW_REGISTER_WHEN_STABLE_MS } from './service-worker.config';

/**
 * Message d'une erreur, sans le reste de l'objet : une réponse HTTP en porte l'URL, les
 * en-têtes et le corps, qui peuvent véhiculer l'abonnement push de l'appareil.
 */
function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  const message = (error as { message?: unknown })?.message;
  return typeof message === 'string' ? message : String(error);
}

/** Marge accordée à l'installation et à l'activation du worker, une fois celui-ci enregistré. */
const SW_ACTIVATION_MARGIN_MS = 10_000;

/** Délai au-delà duquel l'activation du service worker est considérée hors d'atteinte. */
export const SW_READY_TIMEOUT_MS = SW_REGISTER_WHEN_STABLE_MS + SW_ACTIVATION_MARGIN_MS;

/** Convertit une clé VAPID base64url en octets, format attendu par `PushManager.subscribe`. */
export function vapidKeyToBytes(publicKey: string): Uint8Array<ArrayBuffer> {
  const base64 = publicKey.replace(/_/g, '/').replace(/-/g, '+');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const raw = atob(padded);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

/**
 * Cause d'un échec d'activation ou de désactivation des notifications, à charge
 * de `PwaNotificationsService` d'en informer l'utilisateur.
 */
export type PushFailure =
  | 'service-worker-unavailable'
  | 'subscription-rejected'
  | 'registration-failed'
  | 'unregistration-failed'
  | 'unsubscribe-failed'
  | 'unexpected-error';

/**
 * Abonnement aux notifications push (Web Push / VAPID) et envoi de l'abonnement
 * à l'API (`/api/web-push/subscriptions`). Inerte hors des cibles où le service
 * worker est actif (intégration) : `swPush.isEnabled` y est faux.
 *
 * Aucune méthode ne lève : les échecs sont publiés sur {@link failures$}.
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private readonly refresh$ = new BehaviorSubject<void>(undefined);
  private readonly failures = new Subject<PushFailure>();
  private readonly busy = new BehaviorSubject<boolean>(false);
  private pendingEnable: Promise<boolean> | null = null;
  private pendingDisable: Promise<void> | null = null;

  /** Lecture partagée : les abonnés successifs se greffent sur la même interrogation du navigateur. */
  private readonly subscriptionStream$: Observable<PushSubscription | null> = this.refresh$.pipe(
    switchMap(() => from(this.currentSubscription()).pipe(catchError(() => of(null)))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /** Échecs d'activation et de désactivation, destinés à l'affichage. */
  readonly failures$: Observable<PushFailure> = this.failures.asObservable();

  /** Vrai tant qu'une activation ou une désactivation est en cours. */
  readonly busy$: Observable<boolean> = this.busy.asObservable();

  constructor(
    private readonly swPush: SwPush,
    private readonly httpApi: HttpApiService,
  ) {}

  /** Le push est-il techniquement disponible (SW actif + API navigateur présentes) ? */
  get isSupported(): boolean {
    return (
      this.swPush.isEnabled &&
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'PushManager' in window
    );
  }

  /** État de la permission de notification du navigateur. */
  get permission(): NotificationPermission {
    return typeof Notification !== 'undefined' ? Notification.permission : 'denied';
  }

  /**
   * Abonnement push courant de l'appareil. Émet `null` aussi bien quand l'appareil
   * n'est pas abonné que lorsque le service worker n'est pas joignable : le flux ne
   * tombe jamais en erreur, sous peine d'interrompre définitivement les vues qui s'y
   * abonnent via le pipe `async`.
   */
  get subscription$(): Observable<PushSubscription | null> {
    return this.subscriptionStream$;
  }

  /**
   * Efface la pastille de l'icône (posée par le service worker à la réception
   * d'un push) à l'ouverture de l'app et à chaque retour au premier plan.
   */
  initBadgeClearing(): void {
    const nav = navigator as Navigator & { clearAppBadge?: () => Promise<void> };
    if (typeof nav.clearAppBadge !== 'function') {
      return;
    }
    const clear = () => nav.clearAppBadge!().catch(() => undefined);
    clear();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        clear();
      }
    });
  }

  /**
   * Demande la permission, abonne l'appareil et envoie l'abonnement à l'API.
   * Un appel émis pendant qu'une activation est en cours partage son résultat.
   */
  enable(): Promise<boolean> {
    if (!this.swPush.isEnabled) {
      return Promise.resolve(false);
    }
    if (!this.pendingEnable) {
      this.busy.next(true);
      this.pendingEnable = this.runEnable().finally(() => {
        this.pendingEnable = null;
        this.busy.next(false);
        this.refresh$.next();
      });
    }
    return this.pendingEnable;
  }

  /**
   * Désabonne l'appareil : l'abonnement du navigateur n'est retiré qu'une fois l'API à jour.
   * Un appel émis pendant qu'une désactivation est en cours partage son résultat.
   */
  disable(): Promise<void> {
    if (!this.pendingDisable) {
      this.busy.next(true);
      this.pendingDisable = this.runDisable().finally(() => {
        this.pendingDisable = null;
        this.busy.next(false);
        this.refresh$.next();
      });
    }
    return this.pendingDisable;
  }

  private async runDisable(): Promise<void> {
    try {
      const subscription = await this.currentSubscription();
      if (!subscription) {
        return;
      }
      try {
        await firstValueFrom(
          this.httpApi.delete('/web-push/subscriptions', {
            body: { endpoint: subscription.endpoint },
          }),
        );
      } catch (error) {
        console.error('Suppression de l’abonnement auprès de l’API échouée', errorMessage(error));
        this.failures.next('unregistration-failed');
        return;
      }
      const unsubscribed = await subscription.unsubscribe().catch(error => {
        console.error('Désabonnement du navigateur échoué', errorMessage(error));
        return false;
      });
      if (!unsubscribed) {
        this.failures.next('unsubscribe-failed');
      }
    } catch (error) {
      console.error('Désactivation des notifications interrompue', errorMessage(error));
      this.failures.next('unexpected-error');
    }
  }

  private async runEnable(): Promise<boolean> {
    try {
      // iOS impose que la demande de permission parte directement du geste
      // utilisateur : elle précède donc toute attente asynchrone.
      if (this.permission === 'default') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          return false;
        }
      }
      if (this.permission === 'denied') {
        this.failures.next('subscription-rejected');
        return false;
      }
      const registration = await this.activeRegistration();
      if (!registration) {
        console.error(
          `Abonnement aux notifications abandonné : aucun service worker actif après ${SW_READY_TIMEOUT_MS} ms`,
        );
        this.failures.next('service-worker-unavailable');
        return false;
      }
      const options = {
        userVisibleOnly: true,
        applicationServerKey: vapidKeyToBytes(VAPID_PUBLIC_KEY),
      };
      let subscription: PushSubscription;
      try {
        subscription = await registration.pushManager.subscribe(options);
      } catch (error) {
        console.error('Abonnement du navigateur refusé', errorMessage(error));
        this.failures.next('subscription-rejected');
        return false;
      }
      try {
        await this.register(subscription);
      } catch (error) {
        console.error('Enregistrement de l’abonnement auprès de l’API échoué', errorMessage(error));
        await this.rollback(subscription);
        this.failures.next('registration-failed');
        return false;
      }
      return true;
    } catch (error) {
      console.error('Activation des notifications interrompue', errorMessage(error));
      this.failures.next('unexpected-error');
      return false;
    }
  }

  /** Retire du navigateur un abonnement que l'API n'a pas enregistré. */
  private async rollback(subscription: PushSubscription): Promise<void> {
    try {
      if (!(await subscription.unsubscribe())) {
        console.error('Le navigateur a refusé le retrait de l’abonnement non enregistré');
      }
    } catch (error) {
      console.error('Retrait de l’abonnement non enregistré échoué', errorMessage(error));
    }
  }

  /**
   * Abonnement enregistré auprès du navigateur. `null` couvre deux cas indiscernables
   * ici : appareil non abonné, ou service worker hors d'atteinte.
   */
  private async currentSubscription(): Promise<PushSubscription | null> {
    const registration = await this.activeRegistration();
    return registration ? registration.pushManager.getSubscription() : null;
  }

  /**
   * Registration du service worker activé, ou `null` au-delà de {@link SW_READY_TIMEOUT_MS}.
   * L'attente porte sur `navigator.serviceWorker.ready`, qui se résout dès l'activation du
   * worker, sans exiger qu'il contrôle la page courante.
   */
  private async activeRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!this.swPush.isEnabled || typeof navigator === 'undefined' || !navigator.serviceWorker) {
      return null;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const expiration = new Promise<null>(resolve => {
      timer = setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS);
    });
    try {
      return await Promise.race([navigator.serviceWorker.ready, expiration]);
    } finally {
      clearTimeout(timer);
    }
  }

  private async register(subscription: PushSubscription): Promise<void> {
    const json = subscription.toJSON();
    await firstValueFrom(
      this.httpApi.post('/web-push/subscriptions', {
        endpoint: json.endpoint,
        keys: json.keys,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      }),
    );
  }
}
